<template>
  <div v-if="canAccess" class="bg-white border border-gray-200 rounded-lg" data-private>
    <div class="px-4 py-3 bg-primary-50 border-b border-primary-100 rounded-t-lg flex items-center justify-between flex-wrap gap-2">
      <h3 class="text-sm font-semibold text-primary-900">Performance Rankings</h3>
      <div class="flex items-center gap-3">
        <!-- Mode toggle only shown where the tenant has multi-date metric data
             (set showImprovementToggle:true in section.settings). Hidden by
             default so a single-observation tenant like Diverge never surfaces an
             always-empty "Improvement" view. -->
        <div v-if="showImprovementToggle" class="flex items-center gap-1.5 text-xs">
          <button
            @click="mode = 'performance'"
            class="px-2 py-1 rounded transition-colors"
            :class="mode === 'performance' ? 'bg-primary-200 text-primary-900 font-medium' : 'text-gray-500 hover:text-gray-700'"
          >Top / Bottom Performers</button>
          <button
            @click="mode = 'movers'"
            class="px-2 py-1 rounded transition-colors"
            :class="mode === 'movers' ? 'bg-primary-200 text-primary-900 font-medium' : 'text-gray-500 hover:text-gray-700'"
          >Greatest / Least Improvement</button>
        </div>
        <ExportMenu :disabled="!hasExportableRows" :exports="[{ type: 'excel', action: exportTableToExcel }]" />
      </div>
    </div>

    <!-- Filter bar — driven by section.settings.infoTagFilters / entityTypeFilters -->
    <div class="px-4 py-2.5 border-b border-gray-200 flex items-center gap-3 flex-wrap text-xs">
      <div
        v-for="f in infoTagFilters"
        :key="'infotag:' + f.infoTagTypeId"
        class="inline-flex items-center gap-1.5"
      >
        <span class="font-semibold text-gray-600">{{ infoTagTypeNames[f.infoTagTypeId] || `Type ${f.infoTagTypeId}` }}</span>
        <MultiSelectDropdown
          :model-value="infoTagSelections[f.infoTagTypeId] || []"
          @update:model-value="setInfoTagSelection(f.infoTagTypeId, $event)"
          :options="infoTagOptions[f.infoTagTypeId] || []"
          placeholder="All"
          :searchable="true"
          :compact="true"
          :hide-single-value="false"
          max-width="300px"
        />
      </div>

      <div
        v-for="typeId in entityTypeFilters"
        :key="'entity:' + typeId"
        class="inline-flex items-center gap-1.5"
      >
        <span class="font-semibold text-gray-600">{{ entityTypeNames[typeId] || `Type ${typeId}` }}</span>
        <MultiSelectDropdown
          :model-value="entitySelections[typeId] || []"
          @update:model-value="setEntitySelection(typeId, $event)"
          :options="entityOptions[typeId] || []"
          placeholder="All"
          :searchable="true"
          :compact="true"
          :hide-single-value="false"
          max-width="300px"
        />
      </div>

      <button
        @click="canViewAllPractices && (myPracticesOnly = !myPracticesOnly)"
        :disabled="!canViewAllPractices"
        class="inline-flex items-center gap-2 px-3 py-1 rounded border font-medium transition-colors"
        :class="!canViewAllPractices
          ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
          : myPracticesOnly
            ? 'bg-secondary-100 border-secondary-300 text-secondary-900'
            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'"
      >My Practices</button>
      <button
        v-if="hasModifiedFilters"
        @click="resetFilters"
        class="text-xs text-gray-400 hover:text-gray-600 ml-1"
        title="Reset filters to the tenant default"
      >Reset</button>
    </div>

    <div class="px-4 py-2 border-b border-gray-200 flex items-center gap-1 flex-wrap">
      <span class="text-xs font-semibold text-gray-600">Metrics</span>
      <MultiSelectDropdown
        v-model="selectedMetricIds"
        :options="metricDropdownOptions"
        placeholder="+ Metric"
        :searchable="true"
        :compact="true"
        :hide-single-value="true"
        max-width="400px"
      />
      <Tag
        v-for="m in selectedMetricDetails"
        :key="m.id"
        :label="m.label"
        :color="m.locked ? 'gray' : 'secondary'"
        :removable="!m.locked"
        :clickable="true"
        @click="focusMetric(m.id)"
        @remove="removeMetric(m.id)"
      >
        <template v-if="m.locked" #prefix>
          <svg class="w-3 h-3 opacity-60" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
          </svg>
        </template>
      </Tag>
      <button
        v-if="hasModifiedConfigurables"
        @click="resetConfigurables"
        class="text-xs text-gray-400 hover:text-gray-600 ml-1"
        title="Reset configurable metrics to the tenant default"
      >Reset</button>
      <div class="flex-1" />
      <button
        v-if="metricBlocks.length"
        class="text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5 border border-gray-300 rounded"
        @click="toggleExpandAll"
      >{{ allExpanded ? 'Collapse' : 'Expand' }}</button>
    </div>
    
    <LoadingIndicator v-if="loading" message="Loading metrics..." />

    <div v-else-if="allMetricIds.length === 0" class="flex flex-col items-center justify-center py-10 px-6 text-center text-sm text-gray-500">
      <div class="text-gray-500 text-sm">No metrics selected.</div>
    </div>

    <div v-else-if="metricBlocks.length === 0" class="flex items-center justify-center py-10 text-gray-500 text-sm">
      No data available for the selected metrics.
    </div>

    <template v-else>
      <div class="flex flex-col gap-3 p-3">
        <PerformanceTable
          v-for="m in metricBlocks"
          :key="m.metric_id"
          :metric="m"
          :mode="mode"
          :info-tag-columns="resolvedInfoTagColumns"
          :collapsed="collapsedIds.includes(m.metric_id)"
          @drill="drill"
          @toggle-collapse="toggleCollapse(m.metric_id)"
        />
      </div>
      <div class="px-4 py-2 flex items-center border-t border-gray-100">
        <span class="text-xs text-gray-400">Only {{ !canViewAllPractices ? 'your ' : '' }}practices with {{ minDenominator }}+ patients are included.</span>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import MultiSelectDropdown from '@/components/widgetcomponents/library/MultiSelectDropdown.vue';
import Tag from '@/components/widgetcomponents/library/Tag.vue';
import PerformanceTable from '@/components/InsightsNew/PerformanceTable.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { exportToExcel } from '@/utils/exportArraytoXlsx';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';

export default {
  name: 'PerformanceRankings',
  components: { LoadingIndicator, MultiSelectDropdown, Tag, PerformanceTable, ExportMenu },
  props: {
    // { locked: [id, ...], defaultConfigurable: [id, ...] }
    metrics: { type: Object, default: () => ({}) },
    // Array of { infoTagTypeId, defaultTagIds? }
    infoTagFilters: { type: Array, default: () => [] },
    // Array of EntityType IDs
    entityTypeFilters: { type: Array, default: () => [] },
    // Array of InfoTagType IDs to show as columns in the table
    infoTagColumns: { type: Array, default: () => [] },
    year: { type: Number, required: true },
    groupSize: { type: Number, default: 5 },
    minDenominator: { type: Number, default: 10 },
    // Show the Top/Bottom ↔ Greatest/Least Improvement mode toggle. Improvement
    // (movers) needs >=2 observation dates per metric/year, which only tenants
    // with multi-date metric data have (Vytalize/Curana). Hidden by default;
    // those tenants set showImprovementToggle:true in section.settings. Diverge
    // leaves it unset → Top/Bottom only.
    showImprovementToggle: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      metricBlocks: [],
      mode: 'performance',
      myPracticesOnly: true,
      configurableMetricIds: [],
      availableMetrics: [],
      // Metric IDs whose PerformanceTable is currently collapsed. Default
      // empty = everything expanded on first render.
      collapsedIds: [],
      // Gates watcher-driven fetches until mount-time seeding finishes so the
      // first render fires exactly one request.
      ready: false,
      // Monotonic token for fetchData. Used to drop stale responses when a
      // newer fetch is in flight (e.g. user toggles modes rapidly).
      fetchSeq: 0,
      // Becomes true once fetchAvailableMetrics resolves. Suppresses tag
      // rendering until names are available so we don't flash `Metric 278`
      // before swapping in the real display name.
      availableMetricsLoaded: false,
      // All keyed by type ID — type IDs are immutable so admin renames can't
      // break us.
      infoTagSelections: {},    // { typeId: [tag_id, ...] }
      entitySelections: {},     // { typeId: [entity_id, ...] }
      infoTagOptions: {},       // { typeId: [{value, label}, ...] }
      entityOptions: {},        // { typeId: [{value, label}, ...] }
      infoTagTypeNames: {},     // { typeId: 'Practice Tier' }
      entityTypeNames: {},      // { typeId: 'Market' }
      // Effective infoTag defaults captured during fetchFilterOptions, used
      // for both the initial seed and for resetFilters.
      infoTagDefaults: {},      // { typeId: [tag_id, ...] }
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug']),
    canAccess() {
      return canAccessFeature(FEATURES.COMPONENT_ANCHOR_PERFORMANCE_SUMMARY);
    },
    canViewAllPractices() {
      return canAccessFeature(FEATURES.COMPONENT_PRACTICE_PARTNER_ALL);
    },
    lockedMetricIds() {
      return this.metrics.locked || [];
    },
    defaultConfigurableMetricIds() {
      return this.metrics.defaultConfigurable || [];
    },
    allMetricIds() {
      const seen = new Set();
      const merged = [];
      for (const id of [...this.lockedMetricIds, ...this.configurableMetricIds]) {
        if (!seen.has(id)) { seen.add(id); merged.push(id); }
      }
      return merged;
    },
    // Locked metrics aren't user-pickable, so omit them from the picker.
    metricDropdownOptions() {
      const lockedSet = new Set(this.lockedMetricIds);
      return this.availableMetrics
        .filter(m => !lockedSet.has(m.id))
        .map(m => ({
          value: m.id,
          label: m.display_name || m.name || `Metric ${m.id}`,
          group: m.domain_name || 'Other',
        }));
    },
    selectedMetricIds: {
      get() {
        return this.configurableMetricIds;
      },
      set(ids) {
        // Coerce to numbers in case dropdown emits strings; dedupe; assign.
        const seen = new Set();
        this.configurableMetricIds = ids
          .map(Number)
          .filter(n => {
            if (!Number.isFinite(n) || seen.has(n)) return false;
            seen.add(n);
            return true;
          });
      },
    },
    selectedMetricDetails() {
      if (!this.availableMetricsLoaded) return [];
      const lockedSet = new Set(this.lockedMetricIds);
      const byId = new Map(this.availableMetrics.map(m => [m.id, m]));
      return this.allMetricIds.map(id => {
        const m = byId.get(id);
        return {
          id,
          label: m ? (m.display_name || m.name) : `Metric ${id}`,
          locked: lockedSet.has(id),
        };
      });
    },
    storageKey() {
      // Scope per tenant + user so selections don't leak across contexts.
      const slug = this.current_tenant_slug || 'default';
      const uid = this.current_user?.id || 'anon';
      return `anchor_widget_state_${slug}_${uid}`;
    },
    // One inner group per non-empty selection. Backend ORs within, ANDs across.
    infotagIdGroups() {
      return this.infoTagFilters
        .map(f => this.infoTagSelections[f.infoTagTypeId] || [])
        .filter(g => g.length > 0);
    },
    entityIdGroups() {
      return this.entityTypeFilters
        .map(typeId => this.entitySelections[typeId] || [])
        .filter(g => g.length > 0);
    },
    resolvedInfoTagColumns() {
      return this.infoTagColumns.map(id => ({
        typeId: id,
        label: this.infoTagTypeNames[id] || `Type ${id}`,
      }));
    },
    allExpanded() {
      return this.collapsedIds.length === 0;
    },
    hasExportableRows() {
      for (const metric of this.metricBlocks) {
        const g = metric.groups || {};
        if ((g.top || []).length || (g.bottom || []).length || (g.movers || []).length) return true;
      }
      return false;
    },
    hasModifiedConfigurables() {
      const cur = this.configurableMetricIds;
      const def = this.defaultConfigurableMetricIds;
      // Set comparison — reordering the same metrics isn't a modification
      // (mirrors hasModifiedFilters).
      if (cur.length !== def.length) return true;
      const defSet = new Set(def);
      return cur.some(id => !defSet.has(id));
    },
    hasModifiedFilters() {
      // Suppress until mount-time seeding completes so infoTagDefaults reflects
      // the actually-seeded values.
      if (!this.ready) return false;
      // Default is off; "modified" once the user turns it on.
      if (this.myPracticesOnly) return true;
      for (const f of this.infoTagFilters) {
        const cur = this.infoTagSelections[f.infoTagTypeId] || [];
        const def = this.infoTagDefaults[f.infoTagTypeId] || [];
        if (cur.length !== def.length) return true;
        const defSet = new Set(def);
        if (cur.some(id => !defSet.has(id))) return true;
      }
      for (const typeId of this.entityTypeFilters) {
        if ((this.entitySelections[typeId] || []).length > 0) return true;
      }
      return false;
    },
    // Watched as one unit so a single watcher drives fetchData.
    requestSignature() {
      return {
        metricIds: this.allMetricIds,
        year: this.year,
        mode: this.mode,
        infotagGroups: this.infotagIdGroups,
        entityGroups: this.entityIdGroups,
        myPracticesOnly: this.myPracticesOnly,
      };
    },
  },
  watch: {
    // `ready` gate keeps mount-time seeding from triggering a fetch.
    requestSignature() { if (this.ready) this.fetchData(); },
    defaultConfigurableMetricIds(val) {
      if (!this.hasSavedConfigurables()) {
        this.configurableMetricIds = [...val];
      }
    },
    configurableMetricIds() { if (this.ready) this.saveState(); },
    infoTagSelections() { if (this.ready) this.saveState(); },
    entitySelections() { if (this.ready) this.saveState(); },
    myPracticesOnly() { if (this.ready) this.saveState(); },
    mode() { if (this.ready) this.saveState(); },
    collapsedIds() { if (this.ready) this.saveState(); },
    infoTagFilters() { this.fetchFilterOptions(); },
    entityTypeFilters() { this.fetchFilterOptions(); },
    metricBlocks(blocks) {
      // Drop collapsed IDs that no longer correspond to a rendered metric.
      const live = new Set(blocks.map(m => m.metric_id));
      this.collapsedIds = this.collapsedIds.filter(id => live.has(id));
    },
  },
  methods: {
    loadSavedState() {
      // Restores configurable metrics, filter selections, and the My Practices
      // toggle from a single blob. Returns true iff a saved configurable list
      // was applied (so mount/prop watcher can decide whether to seed from
      // defaults).
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return false;
        let loadedConfigurables = false;
        if (Array.isArray(parsed.configurableMetricIds)) {
          // Coerce to numbers (mirrors the selectedMetricIds setter) so a
          // legacy string-typed blob can't break the dedupe in allMetricIds.
          this.configurableMetricIds = parsed.configurableMetricIds
            .map(Number)
            .filter(Number.isFinite);
          loadedConfigurables = true;
        }
        if (parsed.infoTagSelections && typeof parsed.infoTagSelections === 'object') {
          this.infoTagSelections = parsed.infoTagSelections;
        }
        if (parsed.entitySelections && typeof parsed.entitySelections === 'object') {
          this.entitySelections = parsed.entitySelections;
        }
        if (typeof parsed.myPracticesOnly === 'boolean') {
          this.myPracticesOnly = this.canViewAllPractices ? parsed.myPracticesOnly : true;
        }
        // Only restore 'movers' when the toggle is actually available, so a
        // stale blob can't strand a Diverge-style tenant in an empty view.
        if (parsed.mode === 'performance'
            || (parsed.mode === 'movers' && this.showImprovementToggle)) {
          this.mode = parsed.mode;
        }
        if (Array.isArray(parsed.collapsedIds)) {
          // Coerce to numbers; the metricBlocks watcher prunes any IDs that
          // aren't in the current rendered set once data arrives.
          this.collapsedIds = parsed.collapsedIds.map(Number).filter(Number.isFinite);
        }
        return loadedConfigurables;
      } catch {
        return false;
      }
    },
    saveState() {
      try {
        const state = {
          configurableMetricIds: this.configurableMetricIds,
          infoTagSelections: this.infoTagSelections,
          entitySelections: this.entitySelections,
          myPracticesOnly: this.myPracticesOnly,
          mode: this.mode,
          collapsedIds: this.collapsedIds,
        };
        localStorage.setItem(this.storageKey, JSON.stringify(state));
      } catch { /* localStorage full or disabled — ignore */ }
    },
    hasSavedConfigurables() {
      // Existence check that doesn't mutate state, used by the
      // defaultConfigurableMetricIds watcher.
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        return !!(parsed && Array.isArray(parsed.configurableMetricIds));
      } catch {
        return false;
      }
    },
    removeMetric(id) {
      // No-op on locked metrics (template guards too, this is belt-and-suspenders).
      if (this.lockedMetricIds.includes(id)) return;
      this.configurableMetricIds = this.configurableMetricIds.filter(i => i !== id);
    },
    resetConfigurables() {
      this.configurableMetricIds = [...this.defaultConfigurableMetricIds];
    },
    resetFilters() {
      this.infoTagSelections = JSON.parse(JSON.stringify(this.infoTagDefaults));
      this.entitySelections = {};
      this.myPracticesOnly = !this.canViewAllPractices;
    },
    toggleCollapse(metricId) {
      this.collapsedIds = this.collapsedIds.includes(metricId)
        ? this.collapsedIds.filter(id => id !== metricId)
        : [...this.collapsedIds, metricId];
    },
    focusMetric(metricId) {
      // Click on a metric tag: collapse everything else so the clicked metric
      // is the only one expanded.
      this.collapsedIds = this.metricBlocks
        .map(m => m.metric_id)
        .filter(id => id !== metricId);
    },
    toggleExpandAll() {
      this.collapsedIds = this.allExpanded
        ? this.metricBlocks.map(m => m.metric_id)
        : [];
    },
    async fetchAvailableMetrics() {
      // PerformanceDomain endpoint so the picker reflects admin curation. If a
      // metric is in multiple domains, first domain alphabetically wins.
      try {
        const response = await axios.get('/api/performancedomains/');
        const domains = (response.data || [])
          .slice()
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        const seen = new Set();
        const flattened = [];
        for (const domain of domains) {
          for (const link of (domain.composable_metrics || [])) {
            if (seen.has(link.metric_id)) continue;
            seen.add(link.metric_id);
            flattened.push({
              id: link.metric_id,
              name: link.metric_name,
              display_name: link.metric_name,
              domain_name: domain.name,
            });
          }
        }
        this.availableMetrics = flattened;
      } catch (error) {
        console.error('Error fetching available metrics:', error);
        this.availableMetrics = [];
      } finally {
        this.availableMetricsLoaded = true;
      }
    },
    setInfoTagSelection(typeId, ids) {
      this.infoTagSelections = { ...this.infoTagSelections, [typeId]: ids };
    },
    setEntitySelection(typeId, ids) {
      this.entitySelections = { ...this.entitySelections, [typeId]: ids };
    },
    async fetchTypeNameMaps() {
      // Type IDs in PageConfiguration are immutable; we resolve to display
      // names here so admin renames take effect on reload.
      try {
        const [tagTypes, entTypes] = await Promise.all([
          axios.get('/api/infotagtypes/'),
          axios.get('/api/entity-types/'),
        ]);
        const tagMap = {};
        for (const t of (tagTypes.data?.results || tagTypes.data || [])) {
          tagMap[t.id] = t.name;
        }
        this.infoTagTypeNames = tagMap;

        const entMap = {};
        for (const e of (entTypes.data?.results || entTypes.data || [])) {
          entMap[e.id] = e.type_name;
        }
        this.entityTypeNames = entMap;
      } catch (error) {
        console.error('Error fetching type name maps:', error);
      }
    },
    async fetchFilterOptions() {
      const infoTagJobs = this.infoTagFilters.map(async f => {
        try {
          const response = await axios.get('/api/infotags/', {
            params: { infoTagType_id: f.infoTagTypeId },
          });
          const tags = response.data || [];
          this.infoTagOptions = {
            ...this.infoTagOptions,
            [f.infoTagTypeId]: tags.map(t => ({ value: t.id, label: t.name })),
          };
          const defaults = (f.defaultTagIds || []).filter(
            id => tags.some(t => t.id === id),
          );
          this.infoTagDefaults = {
            ...this.infoTagDefaults,
            [f.infoTagTypeId]: defaults,
          };
          const current = this.infoTagSelections[f.infoTagTypeId];
          if ((!current || current.length === 0) && defaults.length) {
            this.setInfoTagSelection(f.infoTagTypeId, defaults);
          }
        } catch (error) {
          console.error(`Error fetching infotags for type ${f.infoTagTypeId}:`, error);
        }
      });

      const entityJobs = this.entityTypeFilters.map(async typeId => {
        const typeName = this.entityTypeNames[typeId];
        if (!typeName) {
          // Type map not yet ready or unknown id; skip silently.
          return;
        }
        try {
          const response = await axios.get('/api/entities/', {
            params: { entity_type_name: typeName },
          });
          const entities = response.data?.results || response.data || [];
          this.entityOptions = {
            ...this.entityOptions,
            [typeId]: entities.map(e => ({ value: e.id, label: e.name })),
          };
        } catch (error) {
          console.error(`Error fetching entities for type ${typeId}:`, error);
        }
      });

      await Promise.all([...infoTagJobs, ...entityJobs]);
    },
    async fetchData() {
      // `loading` starts true so the first render shows a spinner. Both early
      // returns below must clear it, otherwise a section with no metrics (or a
      // user without access) spins "Loading metrics..." forever instead of
      // showing the empty state.
      if (!this.canAccess) {
        this.loading = false;
        return;
      }
      if (!this.allMetricIds.length) {
        this.metricBlocks = [];
        this.loading = false;
        return;
      }
      // Capture this request's token. If a newer fetch fires while we're
      // awaiting, fetchSeq increments and we'll bail on the stale response.
      const seq = ++this.fetchSeq;
      this.loading = true;
      try {
        const response = await axios.post(
          '/api/impact_analytics/composable-metrics/performance-rankings/',
          {
            anchor_metric_ids: this.allMetricIds,
            year: this.year,
            mode: this.mode,
            infotag_id_groups: this.infotagIdGroups,
            entity_id_groups: this.entityIdGroups,
            my_practices: this.myPracticesOnly,
            group_size: this.groupSize,
            min_denominator: this.minDenominator,
          },
        );
        if (seq !== this.fetchSeq) return;
        this.metricBlocks = response.data.metrics || [];
      } catch (error) {
        if (seq !== this.fetchSeq) return;
        if (error.response?.status === 403) {
          this.metricBlocks = [];
        } else {
          console.error('Error fetching anchor-metric summary:', error);
          this.metricBlocks = [];
        }
      } finally {
        // Only the latest request controls the loading flag — otherwise a
        // stale finally could clear loading while a newer fetch is in flight.
        if (seq === this.fetchSeq) this.loading = false;
      }
    },
    exportSectionsFor(metric) {
      // Mirrors PerformanceTable.sections so the export matches what's on screen.
      const groups = metric.groups || {};
      if (this.mode === 'movers') {
        return [
          { title: 'Greatest Improvement', rows: groups.top || [] },
          { title: 'Least Improvement', rows: groups.bottom || [] },
        ];
      }
      const out = [{ title: 'Top Performers', rows: groups.top || [] }];
      if ((groups.movers || []).length) {
        out.push({ title: 'Movers', rows: groups.movers });
      }
      out.push({ title: 'Bottom Performers', rows: groups.bottom || [] });
      return out;
    },
    formatRateForExport(rate, numerator, dc) {
      if (rate == null) return '';
      const cfg = dc || {};
      if (cfg.format === 'count') return Number(numerator || 0);
      if (cfg.format === 'decimal' || cfg.format === 'number') {
        return Number(rate).toFixed(cfg.decimal_places ?? 1);
      }
      return (rate * 100).toFixed(cfg.decimal_places ?? 1) + '%';
    },
    formatEngagementScoreForExport(es) {
      // engagement_score is { composite, sub_scores } — extract the headline
      // number that the CC_Engagement_Score_Badge displays.
      if (es == null) return '';
      const composite = typeof es === 'object' ? es.composite : es;
      return composite != null ? Number(composite).toFixed(1) : '';
    },
    async exportTableToExcel() {
      const rows = [];
      for (const metric of this.metricBlocks) {
        const dc = metric.display_config || {};
        for (const section of this.exportSectionsFor(metric)) {
          for (const row of section.rows) {
            const entry = {
              'Metric': metric.metric_name,
              'Section': section.title,
              'Practice': row.entity_name || '',
            };
            for (const col of this.resolvedInfoTagColumns) {
              entry[col.label] = (row.info_tags?.[col.typeId] || []).join(', ');
            }
            entry['Engagement Score'] = this.formatEngagementScoreForExport(row.engagement_score);
            entry['Days Since Last Engagement'] = row.days_since_last_engagement ?? '';
            entry['Performance'] = this.formatRateForExport(row.rate, row.numerator, dc);
            entry['Patients'] = row.attribution ?? '';
            rows.push(entry);
          }
        }
      }
      if (!rows.length) return;
      const modeLabel = this.mode === 'movers' ? 'movers' : 'top_bottom';
      await exportToExcel(rows, `anchor_performance_${this.year}_${modeLabel}.xlsx`, {
        sheetName: 'Performance Summary',
        columnWidths: {
          'Metric': 220,
          'Section': 150,
          'Practice': 220,
          ...Object.fromEntries(this.resolvedInfoTagColumns.map(c => [c.label, 150])),
          'Engagement Score': 110,
          'Days Since Last Engagement': 130,
          'Performance': 110,
          'Patients': 80,
        },
      });
    },
    drill(row) {
      if (!row?.entity_id) return;
      this.$router.push({
        name: 'Insight - Entity - Metric',
        params: {
          metricId: row.metric_id,
          entityIds: [row.entity_id],
        },
      });
    },
  },
  async mounted() {
    this.fetchAvailableMetrics();
    // Type-name map must resolve before fetchFilterOptions can translate the
    // entity type IDs into the type_name params /api/entities/ expects.
    await this.fetchTypeNameMaps();
    // Load before fetchFilterOptions so any saved selections suppress the
    // default seeding inside that call (it only seeds when current is empty).
    if (!this.loadSavedState()) {
      this.configurableMetricIds = [...this.defaultConfigurableMetricIds];
    }
    // Belt-and-suspenders: if the toggle is hidden, never run in movers mode
    // (covers a config flip after a user previously saved mode='movers').
    if (!this.showImprovementToggle) {
      this.mode = 'performance';
    }
    if (!this.canViewAllPractices) {
      this.myPracticesOnly = true;
    }
    await this.fetchFilterOptions();
    this.ready = true;
    this.fetchData();
  },
  beforeUnmount() {
    // Invalidate any in-flight fetchData so its post-await handlers no-op
    // instead of mutating data on a destroyed component.
    this.fetchSeq += 1;
  },
};
</script>

<style scoped>
.select-chevron {
  appearance: none;
  background-repeat: no-repeat;
  background-size: 10px;
  background-position: right 6px center;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
}
</style>
